const TEN = 10;
const SCIENTIFIC_INTETVAL_1 = 0.2;
const SCIENTIFIC_INTETVAL_2 = 0.4;
const SCIENTIFIC_INTETVAL_3 = 0.5;
const SCIENTIFIC_INTETVAL_4 = 0.6;
const SCIENTIFIC_INTETVAL_5 = 0.8;

/**
 * 根据精度，向上取整
 */
export function ceil(value: number, digit?: number) {
  if (value === 0) {
    return 0;
  }
  const TEN = 10;
  const absValue = Math.abs(value);
  let log10 = Math.floor(Math.log10(absValue)) || 0;
  if (typeof digit === 'number' && !Number.isNaN(digit)) {
    log10 = digit;
  }
  const precision = TEN ** log10;
  return Math.ceil(value / precision) * precision;
}

/**
 * 根据精度，向下取整
 */
export function floor(value: number, digit?: number) {
  if (value === 0) {
    return 0;
  }
  const TEN = 10;
  const absValue = Math.abs(value);
  let log10 = Math.floor(Math.log10(absValue)) || 0;
  if (typeof digit === 'number' && !Number.isNaN(digit)) {
    log10 = digit;
  }
  const precision = TEN ** log10;
  return Math.floor(value / precision) * precision;
}

// 处理小数位过长（除法）
function handleNumberSqr(num1: number, num2: number) {
  const TEN = 10;
  let baseNum1 = 0;
  let baseNum2 = 0;
  try {
    baseNum1 += String(num1).split('.')[1].length;
  } catch (e) {
    baseNum1 = 0;
  }
  try {
    baseNum2 += String(num2).split('.')[1].length;
  } catch (e) {
    baseNum2 = 0;
  }
  return (
    Number(String(num1).replace('.', '')) /
    Number(String(num2).replace('.', '')) /
    TEN ** (baseNum1 - baseNum2)
  );
}

type PositiveNumber = number;

// 科学记数法转换
function scientificNotation(num_: number): string {
  if (!num_) {
    return '0';
  }
  const num = Number(num_);
  if (Number.isNaN(num)) {
    return '0';
  }
  // 以10为底数的n次，n向下取整
  const power = Math.floor(Math.log10(num));
  const figures = TEN ** -power * num;
  return `${figures}e${power}`;
}

/**
 * 科学记数法的刻度取值数组
 * @returns {number[]}
 */
function createIntervalValues(
  deltaArr = [
    0,
    SCIENTIFIC_INTETVAL_1,
    SCIENTIFIC_INTETVAL_2,
    SCIENTIFIC_INTETVAL_3,
    SCIENTIFIC_INTETVAL_4,
    SCIENTIFIC_INTETVAL_5
  ]
): number[] {
  const arr = new Array(TEN).fill(null).map((_, i) => i + 1);
  const intervalValues: number[] = [];
  arr.forEach((i, j) => {
    if (j === arr.length - 1) {
      intervalValues.push(i);
      return;
    }
    deltaArr.forEach((item, index) => {
      if (index < deltaArr.length) {
        intervalValues.push(item + i);
      }
    });
  });
  return intervalValues;
}

const intervalValues = [1, 1.2, 1.4, 1.5, 1.6, 1.8, 2, 2.2, 2.5, 3, 3.5, 4, 4.5, 5, 6, 7, 8, 9, 10];

// 计算科学记数法刻度取值
export function calcScientificNumber(num: PositiveNumber, round = false): PositiveNumber {
  const numStr = scientificNotation(num);
  if (numStr === '0') {
    return 0;
  }
  const numArr = numStr.split('e');
  let figures = Number(Number(numArr[0]).toFixed(1));
  if (!round) {
    figures = Math.ceil(Number(numArr[0]));
  }
  const power = Number(numArr[1]);
  for (let i = 0; i < intervalValues.length; i += 1) {
    const item = intervalValues[i];
    if (item >= figures) {
      figures = item;
      break;
    }
  }
  const standard = `${figures}e${power}`;
  return parseFloat(standard);
}

// 避免js精度异常的保留小数位数n
export function toFixed(m: number, n = 0): number {
  return Math.round(TEN ** n * m) / TEN ** n;
}

function quantityExponent(val) {
  if (val === 0) {
    return 0;
  }

  let exp = Math.floor(Math.log(val) / Math.LN10);
  /**
   * exp is expected to be the rounded-down result of the base-10 log of val.
   * But due to the precision loss with Math.log(val), we need to restore it
   * using 10^exp to make sure we can get val back from exp. #11249
   */

  if (val / 10 ** exp >= 10) {
    exp++;
  }

  return exp;
}
/**
 * find a “nice” number approximately equal to x. Round the number if round = true,
 * take ceiling if round = false. The primary observation is that the “nicest”
 * numbers in decimal are 1, 2, and 5, and all power-of-ten multiples of these numbers.
 *
 * See "Nice Numbers for Graph Labels" of Graphic Gems.
 *
 * @param  val Non-negative value.
 * @param  round
 * @return Niced number
 */

export function nice(val: number, round: boolean) {
  const exponent = quantityExponent(val);
  const exp10 = 10 ** exponent;
  const f = val / exp10; // 1 <= f < 10

  let nf;

  if (round) {
    if (f < 1.5) {
      nf = 1;
    } else if (f < 2.5) {
      nf = 2;
    } else if (f < 3.5) {
      nf = 3;
    } else if (f < 4.5) {
      nf = 4;
    } else if (f < 5.5) {
      nf = 5;
    } else if (f < 6.5) {
      nf = 6;
    } else if (f < 7.5) {
      nf = 7;
    } else if (f < 8.5) {
      nf = 8;
    } else if (f < 9.5) {
      nf = 9;
    } else {
      nf = 10;
    }
  } else if (f < 1) {
    nf = 1;
  } else if (f < 2) {
    nf = 2;
  } else if (f < 3) {
    nf = 3;
  } else if (f < 4) {
    nf = 4;
  } else if (f < 5) {
    nf = 5;
  } else if (f < 6) {
    nf = 6;
  } else if (f < 7) {
    nf = 7;
  } else if (f < 8) {
    nf = 8;
  } else if (f < 9) {
    nf = 9;
  } else {
    nf = 10;
  }

  val = nf * exp10; // Fix 3 * 0.1 === 0.30000000000000004 issue (see IEEE 754).
  // 20 is the uppper bound of toFixed.

  return exponent >= -20 ? +val.toFixed(exponent < 0 ? -exponent : 0) : val;
}

export function isInt(num: number): boolean {
  return num % 1 === 0;
}

export function isNumber(num: unknown): boolean {
  return typeof num === 'number' && !Number.isNaN(num);
}
